import { createSelector, createReducer } from '@reduxjs/toolkit';
import {
  selectAllFromApiState,
  getIncludedData,
  getRequestData,
} from '../helpers/selectorHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import {
  HEARINGTYPE_INCLUDE,
  INVITATIONGROUPMAPPING_INCLUDE,
} from '../../constants/api';
import {
  updateInvitationGroupMappingsInitialize,
  getAllInvitationGroupsSuccess,
  addInvitationGroupMappingsToStore,
  removeInvitationGroupMappingsFromStore,
} from '../actions/api';
import { all, call, fork, takeEvery } from 'redux-saga/effects';
import { generateCreateApiCall } from '../helpers/sagaHelpers';
import { InvitationGroupMappingConnector } from '../connectors/invitationGroupMappingConnector';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { API_REDUCER } from '../../constants/store';

// -------- Overview --------

// The following endpoints exist
//  - PATCH /{hearingTypeId/InvitationGroupMappings

// InvitationGroupMapping is included in the following endpoints
//  - GET /InvitationGroup

// -------- Reducer --------
const invitationGroupMappingReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(getAllInvitationGroupsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const invitationGroupMappings = data.filter(
          (include) => include.type === INVITATIONGROUPMAPPING_INCLUDE,
        );
        return updateDtoList(state, 'all', invitationGroupMappings);
      })
      .addCase(addInvitationGroupMappingsToStore, (state, action) => {
        const invitationGroupMappings = getRequestData(action);
        return updateDtoList(state, 'all', invitationGroupMappings);
      })
      .addCase(removeInvitationGroupMappingsFromStore, (state, action) => {
        const idsToRemove = action.payload;
        return removeItemFromList(state, 'all', idsToRemove);
      });
  },
);

// -------- Sagas ---------
export function* invitationGroupMappingSaga() {
  yield all([fork(updateInvitationGroupMappingSaga)]);
}

function* updateInvitationGroupMappingSaga() {
  yield takeEvery(
    updateInvitationGroupMappingsInitialize.type,
    generateCreateApiCall(InvitationGroupMappingConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.updateInvitationGroupMappings],
        action.payload.hearingTypeId,
        action.payload.dtos,
      );
    }),
  );
}

// -------- Selectors --------
const selectGetAllInvitationGroupMappings = (state) =>
  selectAllFromApiState(state, API_REDUCER.INVITATION_GROUP_MAPPING);

const invitationGroupMappingIdsByHearingTypeId = (hearingTypeId) =>
  createSelector(
    selectGetAllInvitationGroupMappings,
    (invitationGroupMappings) => {
      const invitationGroupMappingsByHearingTypeId = invitationGroupMappings.filter(
        (invitationGroupMapping) =>
          getRelId(invitationGroupMapping, HEARINGTYPE_INCLUDE) ===
          hearingTypeId,
      );
      return invitationGroupMappingsByHearingTypeId.map(
        (invitationGroupMapping) => getEntityId(invitationGroupMapping),
      );
    },
  );

const allInvitationGroupMappingsSelector = createSelector(
  selectGetAllInvitationGroupMappings,
  (invitationGroupMappings) => invitationGroupMappings,
);

// -------- Exporting --------
export {
  allInvitationGroupMappingsSelector,
  invitationGroupMappingIdsByHearingTypeId,
};
export default invitationGroupMappingReducer;
